import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { VendorService } from '../services/vendor.service';
import * as VendorActions from './vendor.actions';

@Injectable()
export class VendorsEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _vendorService = inject(VendorService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all vendors',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Vendors
    getAllVendors$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.loadVendors),
            switchMap(() => {
                return this._vendorService.getAll().pipe(
                    map((res) => {
                        return VendorActions.loadVendorsSuccess({
                            vendors: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            VendorActions.loadVendorsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get All Vendors By Category Name
    getAllVendorsByCategoryName$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.loadVendorsByCategoryName),
            switchMap((action) => {
                return this._vendorService
                    .getByCategoryName(action.categoryName)
                    .pipe(
                        map((res) => {
                            return VendorActions.loadVendorsByCategoryNameSuccess(
                                {
                                    vendors: res.data,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                VendorActions.loadVendorsByCategoryNameFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Vendor
    getVendor$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.loadVendor),
            switchMap((action) => {
                return this._vendorService.getOne(action.id).pipe(
                    map((res) => {
                        return VendorActions.loadVendorSuccess({
                            vendor: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            VendorActions.loadVendorFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Vendor
    createVendor$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.createVendor),
            switchMap((action) => {
                return this._vendorService.create(action.vendorData).pipe(
                    map((res) => {
                        return VendorActions.createVendorSuccess({
                            vendor: res.data,
                            success: 'Vendor created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VendorActions.createVendorFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Vendor
    updateVendor$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.updateVendor),
            switchMap((action) => {
                return this._vendorService
                    .update(action.vendorData, action.id)
                    .pipe(
                        map((res) => {
                            return VendorActions.updateVendorSuccess({
                                vendor: res.data,
                                success: 'Vendor updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                VendorActions.updateVendorFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Vendor
    deleteVendor$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VendorActions.deleteVendor),
            switchMap((action) => {
                return this._vendorService.delete(action.id).pipe(
                    map(() => {
                        return VendorActions.deleteVendorSuccess({
                            id: action.id,
                            success: 'Vendor deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VendorActions.deleteVendorFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onVendorActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VendorActions.createVendorSuccess,
                    VendorActions.updateVendorSuccess,
                    VendorActions.deleteVendorSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onVendorActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VendorActions.loadVendorsFailure,
                    VendorActions.loadVendorsByCategoryNameFailure,
                    VendorActions.loadVendorFailure,
                    VendorActions.createVendorFailure,
                    VendorActions.updateVendorFailure,
                    VendorActions.deleteVendorFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
